import * as React from 'react';
import { useState } from 'react';
import { IoqcType } from '../../../shared/types/record.ts';
import { CalibratedToolDialog } from '../../components/calibratedToolDialog.tsx';
import { IntegerInput } from '../../components/integerInput.tsx';
import { OperatorSelect } from '../../components/operatorSelect.tsx';
import { ProtocolMetaFields } from '../../components/protocol-form/protocolMetaFields.tsx';
import { useFormKeyboardNavigation } from '../../components/protocol-form/useFormKeyboardNavigation.ts';
import { CmmConfig, ProtocolFinal, Record, RecordDraft } from '../../types/sharedTypeImpl.ts';
import { reportError } from '../../utils/errorReporting.ts';
import { formatDatetime } from '../../utils/fieldFormats.ts';
import { openLinkInNewTab } from '../../utils/navigation.ts';
import { IoqcTypeSelect } from './ioqcTypeSelect.tsx';
import { RecordFormParts } from './recordFormParts.tsx';
import { useNewOrderWarning } from './useNewOrderWarning.ts';
import { validateRecordForm } from './validateRecordForm.ts';


interface Props {
    className?: string;
    protocol: ProtocolFinal;
    record: RecordDraft;
    setRecord: (record: RecordDraft) => void;
    selectedPosNr: string | null;
    setSelectedPosNr: (posNr: string) => void;
    showSelectedPosNr: boolean;
    setShowSelectedPosNr: (showSelectedPosNr: boolean) => void;
    isDirty: boolean;
    setIsDirty: (isDirty: boolean) => void;
    isFinishedRecord: boolean;
    isViewMode: boolean;
    isSaving: boolean;
    draftSaveError: string | null;
    prevRecord?: Record;
    nextRecord?: Record;
    cmmConfigs: CmmConfig[] | undefined;
    onDraftSave: () => void;
    onFormSubmit: () => void;
    onCloseClicked: () => void;
    onNavigateRecord: (record: Record) => void;
}

export function RecordForm({
    className,
    protocol,
    record,
    setRecord,
    selectedPosNr,
    setSelectedPosNr,
    showSelectedPosNr,
    setShowSelectedPosNr,
    isDirty,
    setIsDirty,
    isFinishedRecord,
    isViewMode,
    isSaving,
    draftSaveError,
    prevRecord,
    nextRecord,
    cmmConfigs,
    onDraftSave,
    onFormSubmit,
    onCloseClicked,
    onNavigateRecord,
}: Props) {

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [hasTargetQuantityChanged, setHasTargetQuantityChanged] = useState(false);
    const [isPartCountEditEnabled, setIsPartCountEditEnabled] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(true);

    const formRef = useFormKeyboardNavigation();

    useNewOrderWarning({ record, prevRecord, isViewMode, isFinishedRecord });


    const handleTextChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setRecord({
            ...record,
            [event.target.name]: event.target.value
        });
        setIsDirty(true);
    }
    const handleOrderNumberChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setRecord({
            ...record,
            [event.target.name]: event.target.value,
            ...(hasTargetQuantityChanged ? null : { targetQuantity: null })
        });
        setIsDirty(true);
    }
    const handleIntegerChange = (field: keyof Record, value: number | null) => {
        setRecord({
            ...record,
            [field]: value,
        });
        setIsDirty(true);
    }
    const handleTargetQuantityChange = (value: number | null) => {
        setRecord({
            ...record,
            targetQuantity: value,
        });
        setIsDirty(true);
        setHasTargetQuantityChanged(true);
    }
    const handleIoqcTypeChange = (ioqcType: IoqcType) => {
        setRecord({
            ...record,
            ioqcType,
        });
        setIsDirty(true);
    }
    const handleCreatorChange = (operatorInitials: string) => {
        setRecord({
            ...record,
            'user': operatorInitials,
        });
        setIsDirty(true);
    }
    const handleEditorChange = (operatorInitials: string) => {
        if (record.edits == null || record.edits.length === 0) {
            reportError('No edits to update');
            return;
        }

        const newEdits = [...record.edits!];
        newEdits.at(-1)!.editedBy = operatorInitials;
        setRecord({
            ...record,
            edits: newEdits,
        });
        setIsDirty(true);
    }

    const handleCalibratedToolsSubmit = (selectedTools: string[]) => {
        setRecord({
            ...record,
            usedTools: selectedTools,
        });
        setIsDialogOpen(false);
        setIsDirty(true);
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (validateRecordForm(record, protocol)) {
            onFormSubmit();
        }
    }

    const handleDraftSave = () => {
        onDraftSave();
    }

    const handleCloseClicked = (e: React.MouseEvent) => {
        e.preventDefault();
        onCloseClicked();
    }


    const prevEdits = record.edits?.slice(0, -1);
    const currentEdit = record.edits?.at(-1);

    return (
        <div className={`${className} measurement-form`}>
            <button
                onClick={() => openLinkInNewTab('https://berryglade.sharepoint.com/:w:/s/BGNET/EQqUpUnfYEtHkUFOiuILpRMBhOc9JOqz4pizmpFhLq1B5w?e=ECJOby')}
                children='Help'
            />
            <br />

            <form
                ref={formRef}
                onSubmit={handleSubmit}
            >
                <table className='record-form--table' >
                    <tbody>
                        <ProtocolMetaFields
                            protocol={protocol}
                            disabled={true}
                            collapseMinorFields={isCollapsed}
                        />

                        <tr>
                            <td>Measurement start</td>
                            <td>
                                <input
                                    type='text'
                                    value={formatDatetime(record.measurementStart)}
                                    disabled={true}
                                />
                            </td>
                        </tr>
                        {(isViewMode && record.measurementEnd) &&
                            <tr>
                                <td>Measurement end</td>
                                <td>
                                    <input
                                        type='text'
                                        value={formatDatetime(record.measurementEnd)}
                                        disabled={true}
                                    />
                                </td>
                            </tr>
                        }
                        {record.measurementType === 'production' &&
                            <tr>
                                <td>Measurement variant</td>
                                <td>
                                    <input
                                        type='text'
                                        value={record.measurementVariant ?? ''}
                                        disabled={true}
                                    /><br />
                                </td>
                            </tr>
                        }
                        {record.measurementType === 'production' &&
                            <tr>
                                <td>Equipment</td>
                                <td>
                                    <input
                                        type='text'
                                        value={record.equipmentDisplayName}
                                        disabled={true}
                                    /><br />
                                </td>
                            </tr>
                        }
                        {record.measurementType === 'ioqc' &&
                            <tr>
                                <td>Measurement type</td>
                                <td>
                                    <IoqcTypeSelect
                                        value={record.ioqcType}
                                        onChange={handleIoqcTypeChange}
                                        required={true}
                                        disabled={isViewMode}
                                    />
                                </td>
                            </tr>
                        }
                        <tr>
                            <td>Order number</td>
                            <td>
                                <input
                                    type='text'
                                    name='orderNumber'
                                    value={record.orderNumber}
                                    onChange={handleOrderNumberChange}
                                    disabled={isViewMode}
                                /><br />
                            </td>
                        </tr>
                        {record.measurementType === 'ioqc' &&
                            <tr>
                                <td>Purchase order</td>
                                <td>
                                    <input
                                        type='text'
                                        name='purchaseOrder'
                                        value={record.purchaseOrder ?? ''}
                                        onChange={handleTextChange}
                                        disabled={isViewMode}
                                    /><br />
                                </td>
                            </tr>
                        }
                        {record.measurementType === 'production' &&
                            <tr>
                                <td>Target quantity</td>
                                <td>
                                    <IntegerInput
                                        value={record.targetQuantity ?? null}
                                        onChange={handleTargetQuantityChange}
                                        disabled={isViewMode}
                                    /><br />
                                </td>
                            </tr>
                        }
                        {record.measurementType === 'ioqc' &&
                            <tr>
                                <td>Quantity</td>
                                <td>
                                    <IntegerInput
                                        value={record.quantity ?? null}
                                        onChange={handleIntegerChange.bind(null, 'quantity')}
                                        disabled={isViewMode}
                                    /><br />
                                </td>
                            </tr>
                        }
                        {record.measurementType === 'production' &&
                            <tr>
                                <td>Part count</td>
                                <td>
                                    <div style={{ display: 'flex', flexDirection: 'row' }}>
                                        <IntegerInput
                                            style={{ flexGrow: 1 }}
                                            value={record.partCount ?? null}
                                            onChange={handleIntegerChange.bind(null, 'partCount')}
                                            disabled={isViewMode || !isPartCountEditEnabled}
                                        />
                                        {(!isViewMode && !isPartCountEditEnabled) &&
                                            <button
                                                type='button'
                                                onClick={() => setIsPartCountEditEnabled(true)}
                                                children='Edit'
                                            />
                                        }
                                    </div>
                                </td>
                            </tr>
                        }
                        <tr>
                            <td colSpan={2}>
                                <button type='button' onClick={() => setIsCollapsed(!isCollapsed)} >
                                    {isCollapsed ? 'Show more' : 'Show less'}
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <RecordFormParts
                    protocol={protocol}
                    record={record}
                    setRecord={setRecord}
                    selectedPosNr={selectedPosNr}
                    setSelectedPosNr={setSelectedPosNr}
                    showSelectedPosNr={showSelectedPosNr}
                    setShowSelectedPosNr={setShowSelectedPosNr}
                    isViewMode={isViewMode}
                    setIsDirty={setIsDirty}
                    cmmConfigs={cmmConfigs}
                />

                <table className='record-form--table' style={{ marginTop: '12px' }}>
                    <tbody>
                        <tr>
                            <td>Used tools</td>
                            <td>
                                <div style={{ display: 'flex', flexDirection: 'row' }}>
                                    <textarea
                                        style={{ flexGrow: 1 }}
                                        value={record.usedTools.join('\n')}
                                        rows={record.usedTools.length || 1}
                                        disabled={true} />
                                    {!isViewMode &&
                                        <button type='button' onClick={() => setIsDialogOpen(true)}>Update</button>
                                    }
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td>Created by</td>
                            <td>
                                <OperatorSelect
                                    operatorInitials={record.user ?? ''}
                                    disabled={isViewMode || isFinishedRecord}
                                    onChange={handleCreatorChange}
                                    required
                                />
                            </td>
                        </tr>
                        {currentEdit &&
                            <tr>
                                <td>Edited by</td>
                                <td>
                                    {prevEdits?.map((edit, index) =>
                                        <OperatorSelect
                                            key={index}
                                            operatorInitials={edit.editedBy ?? ''}
                                            disabled={true}
                                        />
                                    )}
                                    <OperatorSelect
                                        operatorInitials={currentEdit.editedBy ?? ''}
                                        disabled={isViewMode}
                                        onChange={handleEditorChange}
                                        required
                                    />
                                </td>
                            </tr>
                        }
                        <tr>
                            <td>Comment</td>
                            <td>
                                <textarea
                                    rows={3}
                                    name='comment'
                                    value={record.comment}
                                    onChange={handleTextChange}
                                    disabled={isViewMode}
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div className='button-bar' style={{ marginTop: '20px' }}>
                    {!isViewMode && <button type='submit'>Submit</button>}
                    {(!isViewMode && !isFinishedRecord) && <button type='button' onClick={handleDraftSave}>Save draft</button>}
                    <button onClick={handleCloseClicked}>Close</button>
                    {
                        isSaving ? <span> Saving...</span>
                            : draftSaveError ? <span className='error'> Error: {draftSaveError}</span>
                                : (!isDirty && !isViewMode) && <span> Changes saved</span>
                    }
                    {record.measurementType === 'production' && <>
                        <div style={{ flexGrow: 1 }} />
                        <button
                            type='button'
                            onClick={() => onNavigateRecord(prevRecord!)}
                            disabled={!prevRecord}
                            children='Previous record'
                        />
                        <button
                            type='button'
                            onClick={() => onNavigateRecord(nextRecord!)}
                            disabled={!nextRecord}
                            children='Next record'
                        />
                    </>}
                </div>
            </form>

            <CalibratedToolDialog
                isOpen={isDialogOpen}
                initialSelection={record.usedTools}
                onCancel={() => { setIsDialogOpen(false); }}
                onSubmit={handleCalibratedToolsSubmit} />
        </div>
    );
}
